"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.arrayMaxSquaredDelta = arrayMaxSquaredDelta;
exports.assignClosestArrayPoint = assignClosestArrayPoint;
exports.assignClosestMultiArrayPoint = assignClosestMultiArrayPoint;
exports.assignClosestPoint = assignClosestPoint;
exports.maxSquaredDelta = maxSquaredDelta;
exports.multiArrayMaxSquaredDelta = multiArrayMaxSquaredDelta;

var _math = require("../../math.js");

/**
 * @module ol/geom/flat/closest
 */

/**
 * Returns the point on the 2D line segment flatCoordinates[offset1] to
 * flatCoordinates[offset2] that is closest to the point (x, y).  Extra
 * dimensions are linearly interpolated.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset1 Offset 1.
 * @param {number} offset2 Offset 2.
 * @param {number} stride Stride.
 * @param {number} x X.
 * @param {number} y Y.
 * @param {Array<number>} closestPoint Closest point.
 */
function assignClosest(flatCoordinates, offset1, offset2, stride, x, y, closestPoint) {
  var x1 = flatCoordinates[offset1];
  var y1 = flatCoordinates[offset1 + 1];
  var dx = flatCoordinates[offset2] - x1;
  var dy = flatCoordinates[offset2 + 1] - y1;
  var offset;

  if (dx === 0 && dy === 0) {
    offset = offset1;
  } else {
    var t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);

    if (t > 1) {
      offset = offset2;
    } else if (t > 0) {
      for (var i = 0; i < stride; ++i) {
        closestPoint[i] = (0, _math.lerp)(flatCoordinates[offset1 + i], flatCoordinates[offset2 + i], t);
      }

      closestPoint.length = stride;
      return;
    } else {
      offset = offset1;
    }
  }

  for (var i = 0; i < stride; ++i) {
    closestPoint[i] = flatCoordinates[offset + i];
  }

  closestPoint.length = stride;
}
/**
 * Return the squared of the largest distance between any pair of consecutive
 * coordinates.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} max Max squared delta.
 * @return {number} Max squared delta.
 */


function maxSquaredDelta(flatCoordinates, offset, end, stride, max) {
  var x1 = flatCoordinates[offset];
  var y1 = flatCoordinates[offset + 1];

  for (offset += stride; offset < end; offset += stride) {
    var x2 = flatCoordinates[offset];
    var y2 = flatCoordinates[offset + 1];
    var squaredDelta = (0, _math.squaredDistance)(x1, y1, x2, y2);

    if (squaredDelta > max) {
      max = squaredDelta;
    }

    x1 = x2;
    y1 = y2;
  }

  return max;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Ends.
 * @param {number} stride Stride.
 * @param {number} max Max squared delta.
 * @return {number} Max squared delta.
 */


function arrayMaxSquaredDelta(flatCoordinates, offset, ends, stride, max) {
  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    max = maxSquaredDelta(flatCoordinates, offset, end, stride, max);
    offset = end;
  }

  return max;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Endss.
 * @param {number} stride Stride.
 * @param {number} max Max squared delta.
 * @return {number} Max squared delta.
 */


function multiArrayMaxSquaredDelta(flatCoordinates, offset, endss, stride, max) {
  for (var i = 0, ii = endss.length; i < ii; ++i) {
    var ends = endss[i];
    max = arrayMaxSquaredDelta(flatCoordinates, offset, ends, stride, max);
    offset = ends[ends.length - 1];
  }

  return max;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} maxDelta Max delta.
 * @param {boolean} isRing Is ring.
 * @param {number} x X.
 * @param {number} y Y.
 * @param {Array<number>} closestPoint Closest point.
 * @param {number} minSquaredDistance Minimum squared distance.
 * @param {Array<number>} [opt_tmpPoint] Temporary point object.
 * @return {number} Minimum squared distance.
 */


function assignClosestPoint(flatCoordinates, offset, end, stride, maxDelta, isRing, x, y, closestPoint, minSquaredDistance, opt_tmpPoint) {
  if (offset == end) {
    return minSquaredDistance;
  }

  var i, squaredDistance;

  if (maxDelta === 0) {
    // All points are identical, so just test the first point.
    squaredDistance = (0, _math.squaredDistance)(x, y, flatCoordinates[offset], flatCoordinates[offset + 1]);

    if (squaredDistance < minSquaredDistance) {
      for (i = 0; i < stride; ++i) {
        closestPoint[i] = flatCoordinates[offset + i];
      }

      closestPoint.length = stride;
      return squaredDistance;
    } else {
      return minSquaredDistance;
    }
  }

  var tmpPoint = opt_tmpPoint ? opt_tmpPoint : [NaN, NaN];
  var index = offset + stride;

  while (index < end) {
    assignClosest(flatCoordinates, index - stride, index, stride, x, y, tmpPoint);
    squaredDistance = (0, _math.squaredDistance)(x, y, tmpPoint[0], tmpPoint[1]);

    if (squaredDistance < minSquaredDistance) {
      minSquaredDistance = squaredDistance;

      for (i = 0; i < stride; ++i) {
        closestPoint[i] = tmpPoint[i];
      }

      closestPoint.length = stride;
      index += stride;
    } else {
      // Skip ahead multiple points, because we know that all the skipped
      // points cannot be any closer than the closest point we have found so
      // far.  We know this because we know how close the current point is, how
      // close the closest point we have found so far is, and the maximum
      // distance between consecutive points.  For example, if we're currently
      // at distance 10, the best we've found so far is 3, and that the maximum
      // distance between consecutive points is 2, then we'll need to skip at
      // least (10 - 3) / 2 == 3 (rounded down) points to have any chance of
      // finding a closer point.  We use Math.max(..., 1) to ensure that we
      // always advance at least one point, to avoid an infinite loop.
      index += stride * Math.max((Math.sqrt(squaredDistance) - Math.sqrt(minSquaredDistance)) / maxDelta | 0, 1);
    }
  }

  if (isRing) {
    // Check the closing segment.
    assignClosest(flatCoordinates, end - stride, offset, stride, x, y, tmpPoint);
    squaredDistance = (0, _math.squaredDistance)(x, y, tmpPoint[0], tmpPoint[1]);

    if (squaredDistance < minSquaredDistance) {
      minSquaredDistance = squaredDistance;

      for (i = 0; i < stride; ++i) {
        closestPoint[i] = tmpPoint[i];
      }

      closestPoint.length = stride;
    }
  }

  return minSquaredDistance;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Ends.
 * @param {number} stride Stride.
 * @param {number} maxDelta Max delta.
 * @param {boolean} isRing Is ring.
 * @param {number} x X.
 * @param {number} y Y.
 * @param {Array<number>} closestPoint Closest point.
 * @param {number} minSquaredDistance Minimum squared distance.
 * @param {Array<number>} [opt_tmpPoint] Temporary point object.
 * @return {number} Minimum squared distance.
 */


function assignClosestArrayPoint(flatCoordinates, offset, ends, stride, maxDelta, isRing, x, y, closestPoint, minSquaredDistance, opt_tmpPoint) {
  var tmpPoint = opt_tmpPoint ? opt_tmpPoint : [NaN, NaN];

  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    minSquaredDistance = assignClosestPoint(flatCoordinates, offset, end, stride, maxDelta, isRing, x, y, closestPoint, minSquaredDistance, tmpPoint);
    offset = end;
  }

  return minSquaredDistance;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Endss.
 * @param {number} stride Stride.
 * @param {number} maxDelta Max delta.
 * @param {boolean} isRing Is ring.
 * @param {number} x X.
 * @param {number} y Y.
 * @param {Array<number>} closestPoint Closest point.
 * @param {number} minSquaredDistance Minimum squared distance.
 * @param {Array<number>} [opt_tmpPoint] Temporary point object.
 * @return {number} Minimum squared distance.
 */


function assignClosestMultiArrayPoint(flatCoordinates, offset, endss, stride, maxDelta, isRing, x, y, closestPoint, minSquaredDistance, opt_tmpPoint) {
  var tmpPoint = opt_tmpPoint ? opt_tmpPoint : [NaN, NaN];

  for (var i = 0, ii = endss.length; i < ii; ++i) {
    var ends = endss[i];
    minSquaredDistance = assignClosestArrayPoint(flatCoordinates, offset, ends, stride, maxDelta, isRing, x, y, closestPoint, minSquaredDistance, tmpPoint);
    offset = ends[ends.length - 1];
  }

  return minSquaredDistance;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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